// Native form/harmony editing on the Score (slice 4, PR 3).
//
// These ops replace the store's chart-shaped edit bodies: they write the
// form (and keep every part's measures spliced in step), so an edit never
// round-trips through the lead-sheet projection. That matters beyond purity:
// projecting rebuilds parts from the chart's single melody, so once
// multi-voice content exists (slice 6), any chord edit routed through the
// chart would silently destroy it. Semantics mirror the store's original
// chart ops one-for-one; parity is pinned in formEdit.test.ts.
//
// Every op returns a NEW Score (or null for a recognised no-op) and never
// mutates its input. Callers normalize via the store's commitScore.

import { chartChordSymbol, chordFromType, refFromType } from '../chart';
import type { ChordTypeId } from '../chords';
import type { IRealChord } from '../ireal/types';
import { uid } from '../song';
import { keySignatureOf } from './convert';
import { recordHarmonyEdit, type ReharmProvenance } from './reharm';
import { addF, compareF, frac, measureQuarters, mulF, PPQ } from './duration';
import { voiceTiming } from './timing';
import type { FormMeasure, Part, PartMeasure, Score, VoiceItem } from './types';
import { reflowGeneratedPadding } from './voiceEdit';

// ---------------------------------------------------------------------------
// Metadata
// ---------------------------------------------------------------------------

export function setTitle(score: Score, title: string): Score {
  return { ...score, title };
}

export function setComposer(score: Score, composer: string): Score {
  const trimmed = composer.trim();
  const next = { ...score };
  if (trimmed) next.composer = trimmed;
  else delete next.composer;
  return next;
}

export function setStyle(score: Score, style: string): Score {
  return { ...score, style };
}

export function setTempo(score: Score, tempo: number): Score {
  const nextTempo = Math.max(1, Math.round(tempo));
  return score.tempo === nextTempo ? score : { ...score, tempo: nextTempo };
}

export function setTempoText(score: Score, tempoText: string): Score {
  const trimmed = tempoText.trim();
  if ((score.tempoText ?? '') === trimmed) return score;
  const next = { ...score };
  if (trimmed) next.tempoText = trimmed;
  else delete next.tempoText;
  return next;
}

export function setRepeats(score: Score, n: number, max = 16): Score {
  return { ...score, repeats: Math.max(1, Math.min(max, Math.round(n))) };
}

/** Set the key from the chart's free-text label ("Eb", "G minor", "G-"). */
export function setKeyLabel(score: Score, label: string): Score {
  const keySig = keySignatureOf(label);
  return mapMeasure(score, 0, (m) => {
    const next = { ...m };
    if (keySig) next.keySig = keySig;
    else delete next.keySig;
    return next;
  });
}

/** Empty every measure's chord-symbol lane (the form itself stays). */
export function clearHarmony(score: Score): Score {
  return recordHarmonyEdit(score, {
    ...score,
    form: score.form.map((m) => ({ ...m, harmony: [] })),
  });
}

export function setTimeSignature(score: Score, sig: [number, number]): Score {
  const current = score.form[0]?.timeSig;
  if (current?.[0] === sig[0] && current[1] === sig[1]) return score;
  if (!meterCanHoldExistingContent(score, 0, sig)) return score;
  const next = mapMeasure(score, 0, (m) => ({
    ...m,
    timeSig: [...sig] as [number, number],
  }));
  return reflowGeneratedPadding(next, meterAffectedMeasureIndexes(next, 0)) ?? score;
}

/** A meter change must never leave harmony or a written voice beyond the new
 * barline. Measures after the next explicit meter are unaffected. */
function meterCanHoldExistingContent(
  score: Score,
  startIndex: number,
  sig: [number, number],
): boolean {
  for (const i of meterAffectedMeasureIndexes(score, startIndex)) {
    if (!measureCanHoldExistingContent(score, i, sig)) return false;
  }
  return true;
}

function meterAffectedMeasureIndexes(score: Score, startIndex: number): number[] {
  const indexes: number[] = [];
  for (let i = startIndex; i < score.form.length; i++) {
    if (i > startIndex && score.form[i].timeSig) break;
    indexes.push(i);
  }
  return indexes;
}

// ---------------------------------------------------------------------------
// Harmony (chord symbols on the form)
// ---------------------------------------------------------------------------

export interface AddChordArgs {
  root: string;
  chordType: ChordTypeId;
  beats?: number;
  bass?: string;
  quality?: string;
  noChord?: boolean;
  targetTopNote?: string;
}

/**
 * Append a chord to the insertion measure, rolling into a fresh measure when
 * the bar is full (the fresh bar becomes the next insertion point).
 */
export function addChord(
  score: Score,
  input: AddChordArgs,
  insertionMeasureId: string | null,
): { score: Score; insertionMeasureId: string } {
  const requestedBeats = Math.max(0.25, Math.min(8, input.beats ?? 4));
  let idx = insertionMeasureId
    ? score.form.findIndex((m) => m.id === insertionMeasureId)
    : score.form.length - 1;
  if (idx < 0) idx = score.form.length - 1;
  const measure = score.form[idx];
  const capacity = barBeatsAt(score, idx);
  const beats = Math.min(requestedBeats, capacity);
  const used = measure.harmony.reduce((sum, c) => sum + (c.beats || 0), 0);
  if (measure.harmony.length === 0 || used + beats <= capacity) {
    const chord = authoredChord(input, beats);
    return {
      score: recordHarmonyEdit(
        score,
        mapMeasure(score, idx, (m) => ({ ...m, harmony: [...m.harmony, chord] })),
      ),
      insertionMeasureId: measure.id,
    };
  }
  // A freshly inserted bar inherits the active meter, not the shortened
  // pickup capacity of the full bar that triggered this rollover.
  const freshBeats = Math.min(requestedBeats, meterAt(score, idx)[0]);
  const chord = authoredChord(input, freshBeats);
  const fresh: FormMeasure = { id: uid('m'), harmony: [chord] };
  return {
    score: recordHarmonyEdit(score, spliceMeasure(score, idx + 1, fresh)),
    insertionMeasureId: fresh.id,
  };
}

function authoredChord(input: AddChordArgs, beats: number): IRealChord {
  if (input.noChord) {
    return {
      id: uid('c'),
      root: '',
      quality: '',
      chordType: null,
      beats,
      noChord: true,
      symbol: 'N.C.',
    };
  }
  // chordFromType's general-purpose floor is one beat, but a score pickup can
  // legitimately be shorter. This operation has already bounded the span to
  // the target measure, so retain that exact authored capacity.
  return {
    ...chordFromType(input.root, input.chordType, beats, {
      bass: input.bass,
      quality: input.quality,
      targetTopNote: input.targetTopNote,
    }),
    beats,
  };
}

export interface ChordPatch {
  root?: string;
  chordType?: ChordTypeId;
  beats?: number;
  bass?: string;
  quality?: string;
  parentheses?: IRealChord['parentheses'];
  noChord?: boolean;
  targetTopNote?: string;
}

/** Patch a chord by id. Re-voicing regenerates quality/symbol from the
 *  catalogue while the iReal-only extras (slash bass, alternate, small)
 *  survive. Returns null when the id is unknown. */
export function updateChord(score: Score, id: string, patch: ChordPatch): Score | null {
  const hit = findChord(score, id);
  if (!hit) return null;
  const existing = hit.chord;
  const rootChanged = patch.root !== undefined && patch.root !== existing.root;
  const typeChanged = patch.chordType !== undefined && patch.chordType !== existing.chordType;
  const bassChanged = 'bass' in patch;
  const qualityChanged = 'quality' in patch;
  const noChordChanged = 'noChord' in patch;
  const rebuildingNoChord = noChordChanged && !!existing.noChord && !patch.noChord;
  let updated: IRealChord = { ...existing };
  if (patch.noChord) {
    updated = {
      ...updated,
      root: '',
      quality: '',
      chordType: null,
      noChord: true,
      symbol: 'N.C.',
    };
    delete updated.bass;
    delete updated.targetTopNote;
    delete updated.preferredInversion;
  } else if (rootChanged || typeChanged || bassChanged || qualityChanged || rebuildingNoChord) {
    const bass = bassChanged ? patch.bass?.trim() || undefined : existing.bass;
    const ref =
      rootChanged || typeChanged || qualityChanged || rebuildingNoChord
        ? (() => {
            const root = patch.root?.trim() || existing.root || 'C';
            // Moving an imported root while retaining its printed quality
            // must not invent a playable type for an unmapped chord.
            if (
              rootChanged &&
              existing.chordType === null &&
              !existing.noChord &&
              patch.chordType === undefined &&
              qualityChanged &&
              patch.quality === existing.quality
            ) {
              return {
                root,
                quality: existing.quality,
                chordType: null,
                ...(bass ? { bass } : {}),
              };
            }
            const catalogue = refFromType(
              root,
              patch.chordType ?? existing.chordType ?? 'maj7',
              bass,
            );
            return qualityChanged && patch.quality !== undefined
              ? { ...catalogue, quality: patch.quality }
              : catalogue;
          })()
        : {
            root: existing.root || 'C',
            quality: existing.quality,
            chordType: existing.chordType,
            ...(bass ? { bass } : {}),
          };
    updated = { ...updated, ...ref, noChord: undefined };
    delete updated.noChord;
    if (!bass) delete updated.bass;
  }
  if ('parentheses' in patch) updated.parentheses = patch.parentheses;
  if (updated.noChord) delete updated.parentheses;
  else if (rootChanged || typeChanged || bassChanged || qualityChanged || rebuildingNoChord || 'parentheses' in patch) {
    updated.symbol = chartChordSymbol(updated);
  }
  if (patch.beats !== undefined) {
    const siblingBeats = score.form[hit.measureIndex].harmony.reduce(
      (sum, chord) => sum + (chord.id === id ? 0 : Math.max(0, chord.beats || 0)),
      0,
    );
    const availableBeats = Math.max(0, barBeatsAt(score, hit.measureIndex) - siblingBeats);
    const minimumBeats = Math.min(0.25, availableBeats);
    updated.beats = Math.min(Math.max(minimumBeats, patch.beats), availableBeats);
  }
  if ('targetTopNote' in patch) updated.targetTopNote = patch.targetTopNote || undefined;
  return recordHarmonyEdit(
    score,
    mapMeasure(score, hit.measureIndex, (m) => ({
      ...m,
      harmony: m.harmony.map((c) => (c.id === id ? updated : c)),
    })),
  );
}

export function removeChord(score: Score, id: string): Score | null {
  const hit = findChord(score, id);
  if (!hit) return null;
  return recordHarmonyEdit(
    score,
    mapMeasure(score, hit.measureIndex, (m) => ({
      ...m,
      harmony: m.harmony.filter((c) => c.id !== id),
    })),
  );
}

export interface ReplacementChord {
  root: string;
  chordType: ChordTypeId;
  /** Omitted: the replaced chord's span is shared evenly among the unsized. */
  beats?: number;
  bass?: string;
  quality?: string;
  targetTopNote?: string;
}

/**
 * Replace one chord by one or more, inside exactly the span the original
 * occupied — the seam every reharm move writes through (a tritone sub is one
 * chord for one; a ii–V insertion is two for one). Sized chords must fit the
 * span; unsized ones share what is left, evenly. Null when the id is unknown,
 * the list is empty, or the sized chords overrun the span.
 */
export function replaceChord(
  score: Score,
  id: string,
  chords: readonly ReplacementChord[],
  provenance: ReharmProvenance = { moveId: 'manual' },
): Score | null {
  const hit = findChord(score, id);
  if (!hit || chords.length === 0) return null;
  const span = Math.max(0, hit.chord.beats || 0);
  const sized = chords.reduce((sum, c) => sum + Math.max(0, c.beats ?? 0), 0);
  if (sized > span + 1e-9) return null;
  const unsized = chords.filter((c) => c.beats === undefined).length;
  const share = unsized > 0 ? (span - sized) / unsized : 0;
  if (unsized > 0 && share <= 0) return null;
  const replacement: IRealChord[] = chords.map((c) => ({
    ...chordFromType(c.root, c.chordType, c.beats ?? share, {
      bass: c.bass,
      quality: c.quality,
      targetTopNote: c.targetTopNote,
    }),
    beats: c.beats ?? share,
  }));
  return recordHarmonyEdit(
    score,
    mapMeasure(score, hit.measureIndex, (m) => ({
      ...m,
      harmony: m.harmony.flatMap((c) => (c.id === id ? replacement : [c])),
    })),
    provenance,
  );
}

export function setPreferredInversion(
  score: Score,
  id: string,
  inversion: number | null,
): Score | null {
  const hit = findChord(score, id);
  if (!hit) return null;
  return recordHarmonyEdit(
    score,
    mapMeasure(score, hit.measureIndex, (m) => ({
      ...m,
      harmony: m.harmony.map((c) => {
        if (c.id !== id) return c;
        const next = { ...c };
        if (inversion === null) delete next.preferredInversion;
        else next.preferredInversion = inversion;
        return next;
      }),
    })),
  );
}

export function findChord(
  score: Score,
  id: string,
): { chord: IRealChord; measureIndex: number } | null {
  for (let i = 0; i < score.form.length; i++) {
    const chord = score.form[i].harmony.find((c) => c.id === id);
    if (chord) return { chord, measureIndex: i };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Measures (structure — parts stay spliced in step)
// ---------------------------------------------------------------------------

export type MeasurePatch = Partial<
  Pick<
    FormMeasure,
    | 'open'
    | 'close'
    | 'section'
    | 'sectionRole'
    | 'feel'
    | 'bandCue'
    | 'ending'
    | 'timeSig'
    | 'keySig'
    | 'tempo'
    | 'pickupBeats'
    | 'barRepeat'
    | 'coda'
    | 'segno'
    | 'fermata'
    | 'endMarker'
    | 'staffText'
    | 'staffTextAbove'
    | 'directive'
  >
>;

/** Patch structural fields of a measure by id; `undefined` deletes a field. */
export function patchMeasure(score: Score, id: string, patch: MeasurePatch): Score | null {
  const idx = score.form.findIndex((m) => m.id === id);
  if (idx < 0) return null;
  const next = mapMeasure(score, idx, (m) => {
    const next = { ...m } as Record<string, unknown>;
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined) delete next[k];
      else next[k] = v;
    });
    return next as unknown as FormMeasure;
  });
  const meter = meterAt(next, idx);
  const pickupBeats = patch.pickupBeats;
  if (
    'pickupBeats' in patch &&
    pickupBeats !== undefined &&
    (!Number.isFinite(pickupBeats) || pickupBeats <= 0 || pickupBeats > meter[0])
  ) {
    return score;
  }
  const changesMeter = 'timeSig' in patch;
  const changesPickup = 'pickupBeats' in patch;
  if (changesMeter) {
    if (!meterCanHoldExistingContent(next, idx, meter)) return score;
  }
  if (!changesMeter && !changesPickup) return next;

  if (
    changesPickup &&
    pickupBeats !== undefined &&
    !pickupCanHoldExistingContent(next, idx, pickupBeats, meter)
  ) {
    return score;
  }
  let result = next;
  // A full-length value is not a pickup. Keep the score canonical so export
  // does not mark an otherwise ordinary bar as implicit.
  if (pickupBeats === meter[0]) {
    result = mapMeasure(next, idx, (measure) => {
      const full = { ...measure };
      delete full.pickupBeats;
      return full;
    });
  }
  const affected = changesMeter ? meterAffectedMeasureIndexes(result, idx) : [idx];
  return reflowGeneratedPadding(result, affected) ?? score;
}

/** Insert a fresh empty measure after `id` (or at the end when null),
 *  inserting an empty measure into every part at the same index. */
export function insertMeasureAfter(
  score: Score,
  id: string | null,
): { score: Score; measureId: string } {
  const idx = id ? score.form.findIndex((m) => m.id === id) : score.form.length - 1;
  const at = (idx < 0 ? score.form.length - 1 : idx) + 1;
  const fresh: FormMeasure = { id: uid('m'), harmony: [] };
  return { score: spliceMeasure(score, at, fresh), measureId: fresh.id };
}

/** Delete a measure by id, removing every part's bar at the same index. */
export function deleteMeasure(score: Score, id: string): Score | null {
  const idx = score.form.findIndex((m) => m.id === id);
  if (idx < 0 || score.form.length <= 1) return null;
  const form = score.form.filter((_, i) => i !== idx);
  // The tune-wide meter, key and opening tempo live on measure 1 (the chart
  // model kept them global). Deleting measure 1 hands them to the bar that
  // becomes the first unless it states its own; left to normalizing, a waltz
  // in E♭ turned 4/4 with no key and its new first bar read as a pickup. The
  // deleted bar's own marks (pickup, section, barlines, feel) go with it.
  if (idx === 0) {
    const removed = score.form[0];
    const first = { ...form[0] };
    if (removed.timeSig && !first.timeSig) first.timeSig = [...removed.timeSig];
    if (removed.keySig && !first.keySig) first.keySig = { ...removed.keySig };
    if (removed.tempo !== undefined && first.tempo === undefined) first.tempo = removed.tempo;
    form[0] = first;
  }
  const removedEventIds = new Set<string>();
  const parts = score.parts.map((p) => {
    for (const voice of p.measures[idx]?.voices ?? []) {
      collectEventIds(voice.items, removedEventIds);
    }
    return { ...p, measures: p.measures.filter((_, i) => i !== idx) };
  });
  // A slur, hairpin or octave line anchored in the deleted bar goes with it,
  // as it does when a part or a note is removed. Left behind it points at an
  // event that no longer exists, which validateScore calls an error, and a
  // saved copy carrying one is dropped on the next reload.
  const spanners = score.spanners?.filter(
    (spanner) =>
      !removedEventIds.has(spanner.fromEventId) && !removedEventIds.has(spanner.toEventId),
  );
  return spanners && spanners.length !== score.spanners!.length
    ? { ...score, form, parts, spanners }
    : { ...score, form, parts };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function collectEventIds(items: readonly VoiceItem[], out: Set<string>): void {
  for (const item of items) {
    if (item.kind === 'tuplet') collectEventIds(item.items, out);
    else out.add(item.id);
  }
}

function mapMeasure(score: Score, index: number, fn: (m: FormMeasure) => FormMeasure): Score {
  return {
    ...score,
    form: score.form.map((m, i) => (i === index ? fn(m) : m)),
  };
}

/** Insert a form measure at `at`, adding an empty bar to every part there. */
function spliceMeasure(score: Score, at: number, fresh: FormMeasure): Score {
  const form = [...score.form];
  form.splice(at, 0, fresh);
  const parts: Part[] = score.parts.map((p) => {
    const measures: PartMeasure[] = [...p.measures];
    measures.splice(at, 0, { id: `pm:${fresh.id}`, voices: [] });
    return { ...p, measures };
  });
  return { ...score, form, parts };
}

/** Effective beat capacity, shortened when this is a pickup measure. */
function barBeatsAt(score: Score, index: number): number {
  return score.form[index]?.pickupBeats ?? meterAt(score, index)[0];
}

function meterAt(score: Score, index: number): [number, number] {
  let meter: [number, number] = score.form[0]?.timeSig ?? [4, 4];
  for (let i = 0; i <= index; i++) meter = score.form[i]?.timeSig ?? meter;
  return meter;
}

/** A pickup must still contain every authored harmony and voice event. */
function pickupCanHoldExistingContent(
  score: Score,
  index: number,
  pickupBeats: number,
  meter: [number, number],
): boolean {
  return measureCanHoldExistingContent(score, index, meter, pickupBeats);
}

/** Capacity checks concern authored content. The editor pads sparse voices to
 * the old barline with deterministic `r:` / `r-` rests; those rests retain
 * timing positions but must not themselves prevent shortening the bar. */
function measureCanHoldExistingContent(
  score: Score,
  index: number,
  meter: [number, number],
  beatCapacity = score.form[index].pickupBeats ?? meter[0],
): boolean {
  const pickupBeats = score.form[index].pickupBeats;
  if (pickupBeats !== undefined && pickupBeats > meter[0]) return false;
  if (!Number.isFinite(beatCapacity) || beatCapacity <= 0) return false;
  const harmonyBeats = score.form[index].harmony.reduce(
    (sum, chord) => sum + Math.max(0, chord.beats || 0),
    0,
  );
  if (harmonyBeats > beatCapacity + 1e-9) return false;

  const writtenCapacity =
    beatCapacity === meter[0]
      ? measureQuarters(meter)
      : mulF(frac(Math.round(beatCapacity * PPQ), PPQ), frac(4, meter[1]));
  const bandCue = score.form[index].bandCue;
  if (bandCue?.duration && compareF(bandCue.duration, writtenCapacity) > 0) {
    return false;
  }
  for (const part of score.parts) {
    for (const voice of part.measures[index]?.voices ?? []) {
      for (const timed of voiceTiming(voice)) {
        const generatedPadding = timed.event.kind === 'rest' && /^r[:-]/.test(timed.event.id);
        if (generatedPadding) continue;
        if (compareF(addF(timed.start, timed.duration), writtenCapacity) > 0) return false;
      }
    }
  }
  return true;
}
